// Cây thụt lề của sơ đồ mạng lưới (docs/KE_HOACH_NPP_SANG_WEB.md §10 — phương án A, chốt 21/10) — điện thoại / iPad
// dùng (máy tính là cây ngang, NetworkCanvas). Mỗi hàng: các cột đường kẻ nối cấp trên, nút mở / gập, rồi ô đơn vị
// bấm để chọn. Các hàng sát nhau (không có khe) để đường kẻ liền mạch. Dựng cây + tính đường kẻ ở
// @shoptrack/common/network-tree. Dùng chung cho NPP (/npp/network) và Đại lý / CTV (/network): trang truyền sẵn
// việc cần xử lý + dòng phụ của từng đơn vị (dữ liệu hai API khác nhau).

import { ChevronRight } from 'lucide-react'
import {
  NETWORK_TYPE_LABEL, networkUnitTitle,
  type NetworkGuide, type NetworkIssue, type NetworkRow, type NetworkUnitBase, type NetworkUnitType,
} from '@shoptrack/common/network-tree'
import { cn } from '@/lib/utils'
import { Pill } from '@/npp/components/PageIntro'

/** Nền nhãn việc cần xử lý — Pill dùng `bg-…/12`, độ mờ 12 không có trong thang Tailwind nên không ra CSS (nhãn mất nền). */
const PILL_BG = { danger: 'bg-danger/10', warn: 'bg-warn/10' } as const

export const NETWORK_TYPE_META: Record<NetworkUnitType, { label: string; short: string; tone: string }> = {
  agency: { label: NETWORK_TYPE_LABEL.agency, short: 'ĐL', tone: 'tone-orange' },
  ctv: { label: NETWORK_TYPE_LABEL.ctv, short: 'CTV', tone: 'tone-violet' },
  shop: { label: NETWORK_TYPE_LABEL.shop, short: 'S', tone: 'tone-blue' },
}

/** Màu + chữ tắt của loại — loại lạ (dữ liệu lệch) coi như Shop, không làm trắng trang. */
export function networkTypeMeta(type: string): (typeof NETWORK_TYPE_META)[NetworkUnitType] {
  return NETWORK_TYPE_META[type as NetworkUnitType] ?? NETWORK_TYPE_META.shop
}

/** Ô màu loại: mã đơn vị (DL01, CTV12, S07) khi có, không thì chữ tắt loại. */
export function NetworkTypeBadge({ type, code, className }: { type: NetworkUnitType; code?: string | undefined; className?: string }) {
  const meta = networkTypeMeta(type)
  return (
    <span
      aria-hidden={code ? undefined : true}
      className={cn(meta.tone, 'flex h-7 min-w-7 shrink-0 items-center justify-center rounded-lg bg-toneBg px-1 text-2xs font-bold tabular-nums text-toneFg', className)}
    >
      {code ?? meta.short}
    </span>
  )
}

function Guide({ g }: { g: NetworkGuide }) {
  return (
    <span aria-hidden className="relative w-6 shrink-0 mobile:w-5">
      {(g === 'pipe' || g === 'tee') && <span className="absolute inset-y-0 left-1/2 border-l-[1.5px] border-muted/35" />}
      {g === 'tee' && <span className="absolute left-1/2 top-1/2 w-1/2 border-t-[1.5px] border-muted/35" />}
      {g === 'elbow' && <span className="absolute left-1/2 top-0 h-1/2 w-1/2 rounded-bl-md border-b-[1.5px] border-l-[1.5px] border-muted/35" />}
    </span>
  )
}

export function NetworkRowItem<U extends NetworkUnitBase>({
  row,
  selected,
  compact,
  issues,
  sub,
  meta,
  onToggle,
  onSelect,
}: {
  row: NetworkRow<U>
  selected: boolean
  /** Điện thoại: bỏ cột số đơn, nhãn việc cần xử lý xuống dòng phụ để tên đủ chỗ. */
  compact: boolean
  /** Việc cần xử lý của đơn vị (networkUnitIssues với cây NPP). */
  issues: readonly NetworkIssue[]
  /** Dòng phụ dưới tên (Shop: tên chủ; Đại lý / CTV: số cấp dưới). */
  sub: string
  /** Cột phải khi không gọn (số đơn 30 ngày). */
  meta?: string
  onToggle: (id: string) => void
  onSelect: (id: string) => void
}) {
  const { node } = row
  const u = node.unit
  const hasKids = node.children.length > 0

  return (
    <li className={cn('flex min-h-12 items-stretch rounded-lg mobile:min-h-[3.25rem]', selected ? 'bg-accent/10' : row.hit && 'bg-warn/10')}>
      {row.guides.map((g, i) => <Guide key={i} g={g} />)}
      <span className="flex w-8 shrink-0 items-center justify-center">
        {hasKids && !row.toggleable && (
          // Đang lọc: cây tự mở theo kết quả, mũi tên chỉ để nhìn
          <ChevronRight className={cn('size-4 text-muted/50', row.open && 'rotate-90')} aria-hidden />
        )}
        {hasKids && row.toggleable && (
          // Nút nhìn thấy 28px, vùng bấm 44px (giao kèo vùng chạm) — ::before nới ra mỗi bên 8px.
          <button
            type="button"
            onClick={() => onToggle(u.id)}
            aria-expanded={row.open}
            aria-label={`${row.open ? 'Gập' : 'Mở'} cấp dưới của ${u.name}`}
            className="motion-state relative flex size-7 items-center justify-center rounded-md text-muted before:absolute before:-inset-2 hover:bg-paperSoft hover:text-ink"
          >
            <ChevronRight className={cn('size-4 transition-transform', row.open && 'rotate-90')} aria-hidden />
          </button>
        )}
      </span>
      <button
        type="button"
        onClick={() => onSelect(u.id)}
        aria-current={selected || undefined}
        className="motion-state flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-1.5 pl-0.5 pr-2.5 text-left hover:bg-paperSoft/70"
      >
        <NetworkTypeBadge type={u.tenantType} code={u.code} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-ink">
            {networkUnitTitle(u) !== u.name && <span className="sr-only">{networkTypeMeta(u.tenantType).label} </span>}
            {u.name}
          </span>
          <span className="flex min-w-0 items-center gap-1.5">
            {compact && issues[0] && <Pill tone={issues[0].severity} className={cn('px-1.5 py-0 text-2xs', PILL_BG[issues[0].severity])}>{issues[0].label}</Pill>}
            <span className="truncate text-xs text-muted">{sub}</span>
          </span>
        </span>
        {!compact && (
          <span className="flex shrink-0 items-center gap-2">
            {issues.map((i) => <Pill key={i.label} tone={i.severity} className={PILL_BG[i.severity]}>{i.label}</Pill>)}
            <span className="w-24 text-right text-xs tabular-nums text-inkSoft">{meta ?? '—'}</span>
          </span>
        )}
      </button>
    </li>
  )
}
